import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import logoAsset from "@/assets/simply-approved-logo-horizontal.png.asset.json";
const logo = logoAsset.url;
import { navProducts as products, navStates as states } from "@/data/nav-catalog";
import { SITE } from "@/data/site";
import { productApplyHref } from "@/lib/utils";
import { useI18n } from "@/lib/i18n/useLocale";
import { localizedPath } from "@/lib/i18n/routes";
import { LanguageSwitcher } from "./LanguageSwitcher";

const CALCULATORS: { to: string; label: string; blurb: string }[] = [
  { to: "/dscr-calculator", label: "DSCR Calculator", blurb: "Test whether the rent covers the payment before you make an offer." },
  { to: "/rental-property-deal-analyzer", label: "Rental Property Deal Analyzer", blurb: "Underwrite the whole deal: DSCR, NOI, cap rate, cash flow and refinance proceeds." },
  { to: "/brrrr-calculator", label: "BRRRR Calculator", blurb: "Model buy, rehab, rent, refinance and see the cash left in the deal." },
  { to: "/cash-on-cash-return-calculator", label: "Cash-on-Cash Calculator", blurb: "See the year-one return on the cash you actually put into the deal." },
  { to: "/cap-rate-calculator", label: "Cap Rate Calculator", blurb: "Build NOI line by line and price the asset without financing." },
  { to: "/arv-calculator", label: "ARV Calculator", blurb: "Estimate after-repair value from comps and screen the 70% rule." },
  { to: "/rent-needed-for-dscr", label: "Rent Needed for DSCR", blurb: "Find the rent required to hit a 1.00, 1.10 or 1.25 ratio." },
  { to: "/dscr-payment-calculator", label: "DSCR Payment Calculator", blurb: "Full monthly PITIA on an investment property loan." },
  { to: "/ltv-equity-calculator", label: "LTV & Equity Calculator", blurb: "LTV, CLTV, equity, and proceeds at your target leverage." },
  { to: "/closing-cost-calculator", label: "Closing Cost Calculator", blurb: "Estimate cash to close, prepaids, and escrow reserves." },
  { to: "/cash-out-refinance-calculator", label: "Cash-Out Refinance Calculator", blurb: "Proceeds, new payment, DSCR impact, and break-even." },
  { to: "/prepayment-penalty-calculator", label: "Prepayment Penalty Calculator", blurb: "Cost of a 5/4/3/2/1 step-down at your planned exit year." },
  { to: "/interest-only-vs-amortizing-calculator", label: "Interest-Only vs Amortizing", blurb: "Cash flow, DSCR, and principal paydown side by side." },
  { to: "/rates", label: "Today's Investor Rates", blurb: "Current benchmark indices and indicative investor pricing ranges." },
  { to: "/investment-property-rates", label: "Live Rate Center", blurb: "Price your own DSCR scenario and compare live rate, APR, points and credits." },
  { to: "/dscr-heloc-rates", label: "DSCR HELOC Pricing", blurb: "Price a 1st or 2nd lien equity line on a non-primary property." },
  { to: "/dscr-rate-history", label: "DSCR Rate History", blurb: "Track purchase, cash-out and rate-term pricing over the last 30–90 days." },

];

const DSCR_PROGRAMS: { to: string; label: string }[] = [
  { to: "/dscr-loans", label: "DSCR Loans" },
  { to: "/how-dscr-works", label: "How DSCR Works" },
  { to: "/dscr-loan-rates", label: "DSCR Loan Rates" },
  { to: "/dscr-loan-requirements", label: "DSCR Requirements" },
  { to: "/dscr-loan-pros-and-cons", label: "DSCR Pros & Cons" },
  { to: "/dscr-cash-out-refinance", label: "DSCR Cash-Out Refinance" },
  { to: "/dscr-rate-term-refinance", label: "DSCR Rate & Term Refinance" },
  { to: "/interest-only-dscr-loans", label: "Interest-Only DSCR Loans" },
  { to: "/dscr-heloc", label: "Investment Property HELOC" },
  { to: "/dscr-second-lien-heloc", label: "Second-Lien DSCR HELOC" },
  { to: "/no-ratio-dscr-loans", label: "No-Ratio DSCR Loans" },
];

// Loan pages that aren't in the products data set but belong in the loans menu.
const EXTRA_LOAN_PAGES: { to: string; label: string; blurb: string }[] = [
  { to: "/mid-term-rental-loans", label: "Mid-Term Rental Loans", blurb: "30–90 day furnished rentals underwritten on lease income." },
];


const RESOURCES: { to: string; label: string; blurb: string }[] = [
  {
    to: "/tools/compare-cities",
    label: "Compare Investor Cities",
    blurb: "Line up two or more cities on median home value, rents, HUD Fair Market Rents, cap-rate band and landlord laws to see which market cash-flows best.",
  },
  { to: "/best-rental-markets", label: "Best Rental Markets", blurb: "Where investor deals are penciling right now, ranked by cash flow." },
  { to: "/compare", label: "Loan Comparisons", blurb: "DSCR vs conventional, hard money, BRRRR, STR and more — side by side." },
  { to: "/process", label: "Loan Process", blurb: "What happens from scenario review to funding, step by step." },
  { to: "/section-8-investing-map", label: "Section 8 Investing Map", blurb: "Voucher demand, HUD FMRs and PHA contacts county by county." },
  { to: "/hud-fair-market-rents", label: "HUD Fair Market Rents", blurb: `FY${HUD_DISPLAY_FY} FMR lookups by state, county and ZIP.` },
  { to: "/hud-income-limits", label: "HUD Income Limits", blurb: "Area income limits by state and county for voucher and affordable deals." },
  { to: "/qualified-census-tracts", label: "Qualified Census Tracts", blurb: "QCT lookups for incentive-eligible investment areas." },
  { to: "/difficult-development-areas", label: "Difficult Development Areas", blurb: "DDA designations by state and metro area." },
  { to: "/glossary", label: "Investor Glossary", blurb: "Plain-English definitions of DSCR, LTC, ARV, HAP and more." },
  { to: "/faq", label: "FAQ", blurb: "The questions investors ask us most, answered." },
  { to: "/about", label: "About Us", blurb: "Who we are, how we broker, and the licenses behind the desk." },
  { to: "/contact", label: "Contact", blurb: "Reach a licensed loan officer or visit one of our offices." },
];

const INVESTOR_JOURNEY: { to: string; label: string; blurb: string }[] = [
  { to: "/investor-journey", label: "The Investor Journey", blurb: "From first rental to a financed portfolio — every stage and what we fund at each one." },
  { to: "/business-setup/llc-journey", label: "Buying in an LLC: Step by Step", blurb: "The full order of operations — form the LLC, EIN, ITIN, banking, financing, closing." },
  { to: "/business-setup", label: "Business Setup Hub", blurb: "Get the entity, banking, insurance and title pieces in place before you close." },
  { to: "/process", label: "Our Loan Process", blurb: "Scenario review, term sheet, underwriting, closing — what to expect." },
  { to: "/dscr-calculator", label: "Check Your Deal First", blurb: "Run the DSCR before you write the offer to see where the loan lands." },
];


const BUSINESS_SETUP: { to: string; label: string; blurb: string }[] = [
  { to: "/business-setup", label: "Business Setup Hub", blurb: "Everything you need in place before closing in an entity." },
  { to: "/business-setup/llc-journey", label: "Buying in an LLC: Step by Step", blurb: "The full order of operations — LLC, EIN, ITIN, banking, financing, closing." },
  { to: "/business-setup/llc", label: "Set Up Your LLC", blurb: "Form the entity we can vest your investment property in." },
  { to: "/business-setup/business-bank-account", label: "Business Bank Account", blurb: "Open the account your reserves and rents flow through." },
  { to: "/business-setup/business-loans", label: "Business Loans & Credit", blurb: "Working capital under the entity that helps build business credit." },
  { to: "/business-setup/duns-number", label: "DUNS Number", blurb: "Register the ID your business credit file is built on." },
  { to: "/business-setup/landlord-insurance", label: "Landlord Insurance", blurb: "Investor coverage with loss of rents and the lender's mortgagee clause." },
  { to: "/business-setup/property-management-software", label: "Property Management Software", blurb: "Rent collection, leases, and owner reporting in one place." },
  { to: "/business-setup/itin", label: "ITIN for Non-US Clients", blurb: "Get the tax ID foreign national borrowers need." },
  { to: "/business-setup/title-services", label: "Title & Escrow", blurb: "Title search, escrow, and closing for entity-vested deals." },
];

const MAIN_LINKS: { labelKey: string; to: string }[] = [
  { labelKey: "nav.mortgageLoans", to: "/dscr-loans" },
  { labelKey: "nav.investorJourney", to: "/investor-journey" },
  { labelKey: "nav.whereWeLend", to: "/where-we-lend" },
  { labelKey: "nav.calculators", to: "/dscr-calculator" },
  { labelKey: "nav.resources", to: "/glossary" },
  { labelKey: "nav.businessSetup", to: "/business-setup" },
  { labelKey: "nav.blog", to: "/blog" },
  { labelKey: "nav.partners", to: "/partners" },
];

function useLocalizedTo(enPath: string) {
  const { locale } = useI18n();
  return localizedPath(enPath, locale);
}

export function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const applyHref = productApplyHref(loc.pathname);
  const { t, locale, isSpanish, href } = useI18n();
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto max-w-7xl px-4">
        {/* Top row: logo + phone + CTA + mobile toggle */}
        <div className="flex h-16 items-center justify-between gap-3">
          <Link to={(isSpanish ? "/es" : "/") as "/"} className="flex shrink-0 items-center" aria-label="Simply Approved Mortgages — Home">
            <img src={logo} alt="Simply Approved Mortgages" className="h-9 w-auto md:h-10" width={1196} height={244} />
          </Link>

          <div className="hidden shrink-0 items-center gap-3 lg:flex">
            <LanguageSwitcher />
            <a href={SITE.phoneHref} className="whitespace-nowrap text-sm font-semibold text-brand hover:text-brand-dark">
              {SITE.phone}
            </a>
            <Link
              to={applyHref as "/apply"}
              className="whitespace-nowrap rounded-md bg-brand-gold px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-brand-gold-hover"
            >
              {t("nav.startMyApproval")}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-brand lg:hidden"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Bottom row: full desktop navigation */}
        <nav className="hidden items-center justify-between gap-1 border-t border-border py-2 lg:flex lg:gap-2 xl:gap-4">
          <ProductsMenu />
          <DropdownMenu labelKey="nav.investorJourney" items={INVESTOR_JOURNEY} width="w-96" href="/investor-journey" align="left" />
          <WhereWeLendMenu />
          <DropdownMenu labelKey="nav.calculators" items={CALCULATORS} width="w-80" />
          <DropdownMenu labelKey="nav.resources" items={RESOURCES} width="w-96" align="right" />
          <DropdownMenu labelKey="nav.businessSetup" items={BUSINESS_SETUP} width="w-80" href="/business-setup" align="right" />
          <LocalizedNavItem to="/blog">{t("nav.blog")}</LocalizedNavItem>
          <LocalizedNavItem to="/partners">{t("nav.partners")}</LocalizedNavItem>
        </nav>
      </div>

      {open && (
        <div className="border-t border-border bg-background lg:hidden">
           <div className="container mx-auto flex max-h-[calc(100dvh-4rem)] max-w-7xl flex-col gap-1 overflow-y-auto overscroll-contain px-4 pb-24 pt-4">
            <MobileGroup labelKey="nav.mortgageLoans">
              {products.map((p) => (
                <LocalizedMobileLink key={p.slug} to={`/${p.slug}` as string} onClick={() => setOpen(false)}>
                  {p.name}
                </LocalizedMobileLink>
              ))}
              {EXTRA_LOAN_PAGES.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
              <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.dscrPrograms")}</div>
              {DSCR_PROGRAMS.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
            </MobileGroup>
            <MobileGroup labelKey="nav.investorJourney">
              {INVESTOR_JOURNEY.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
            </MobileGroup>
            <MobileGroup labelKey="nav.whereWeLend">
              <LocalizedMobileLink to="/where-we-lend" onClick={() => setOpen(false)}>{t("nav.allStatesCounties")}</LocalizedMobileLink>
              <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.loanOptionsByLocation")}</div>
              {products.map((p) => (
                <LocalizedMobileLink key={p.slug} to={`/${p.slug}` as string} onClick={() => setOpen(false)}>
                  {p.shortName}
                </LocalizedMobileLink>
              ))}
              <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.states")}</div>
              <div className="grid grid-cols-2 gap-1">
                {states.map((s) => (
                  <Link
                    key={s.slug}
                    to={localizedPath("/dscr-loans/$state", locale) as string}
                    params={{ state: s.slug }}
                    onClick={() => setOpen(false)}
                    className="min-w-0 truncate rounded px-2 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </MobileGroup>

            <MobileGroup labelKey="nav.calculators">
              {CALCULATORS.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
            </MobileGroup>
            <MobileGroup labelKey="nav.resources">
              {RESOURCES.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
            </MobileGroup>
            <MobileGroup labelKey="nav.businessSetup">
              {BUSINESS_SETUP.map((i) => (
                <LocalizedMobileLink key={i.to} to={i.to} onClick={() => setOpen(false)}>{i.label}</LocalizedMobileLink>
              ))}
            </MobileGroup>

            <LocalizedMobileLink to="/blog" onClick={() => setOpen(false)}>{t("nav.blog")}</LocalizedMobileLink>
            <LocalizedMobileLink to="/partners" onClick={() => setOpen(false)}>{t("nav.partners")}</LocalizedMobileLink>
            <LocalizedMobileLink to="/about" onClick={() => setOpen(false)}>{t("nav.about")}</LocalizedMobileLink>
            <LocalizedMobileLink to="/contact" onClick={() => setOpen(false)}>{t("nav.contact")}</LocalizedMobileLink>
            <LanguageSwitcher className="justify-center" />
            <Link
              to={applyHref as "/apply"}
              onClick={() => setOpen(false)}
              className="mt-3 rounded-md bg-brand-gold px-4 py-3 text-center text-base font-semibold text-primary-foreground"
            >{t("nav.startMyApproval")}</Link>
            <a
              href={SITE.phoneHref}
              className="rounded-md border border-brand/30 px-4 py-3 text-center text-base font-semibold text-brand"
            >{t("nav.call")} {SITE.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
}

function LocalizedNavItem({ to, children }: { to: string; children: React.ReactNode }) {
  const localizedTo = useLocalizedTo(to);
  return (
    <Link
      to={localizedTo}
      className="shrink-0 whitespace-nowrap text-[13px] font-medium text-foreground/80 transition-colors hover:text-brand lg:text-sm"
    >
      {children}
    </Link>
  );
}

function MobileGroup({ labelKey, children }: { labelKey: string; children: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <details className="rounded-md border border-border/60">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md px-3 py-3 text-base font-medium text-foreground marker:hidden hover:bg-muted [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 truncate">{t(labelKey as any)}</span>
        <Chevron />
      </summary>
      <div className="flex flex-col gap-0.5 border-t border-border/60 px-2 py-2">{children}</div>
    </details>
  );
}

function LocalizedMobileLink({ to, children, onClick }: { to: string; children: React.ReactNode; onClick: () => void }) {
  const localizedTo = useLocalizedTo(to);
  return (
    <Link
      to={localizedTo}
      onClick={onClick}
      className="rounded-md px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted"
    >
      {children}
    </Link>
  );
}

function Chevron() {
  return (
    <svg className="inline-block h-3.5 w-3.5 -translate-y-px opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export type MenuAlign = "left" | "center" | "right";

export function alignClasses(align: MenuAlign) {
  if (align === "left") return "left-0";
  if (align === "right") return "right-0";
  return "left-1/2 -translate-x-1/2";
}

function DropdownMenu({
  labelKey,
  items,
  width,
  href,
  align = "center",
}: {
  labelKey: string;
  items: { to: string; label: string; blurb: string }[];
  width: string;
  href?: string;
  align?: MenuAlign;
}) {
  const { t, locale } = useI18n();
  const localizedHref = href ? localizedPath(href, locale) : undefined;
  const triggerClass = "flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-foreground/80 transition-colors hover:text-brand lg:text-sm";
  return (
    <div className="group relative">
      {localizedHref ? (
        <Link to={localizedHref} className={triggerClass}>
          {t(labelKey as any)} <Chevron />
        </Link>
      ) : (
        <button className={triggerClass}>
          {t(labelKey as any)} <Chevron />
        </button>
      )}

      <div className={`invisible absolute ${alignClasses(align)} top-full z-50 ${width} max-w-[calc(100vw-2rem)] pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100`}>
        <div className="max-h-[calc(100vh-6rem)] overflow-y-auto rounded-lg border border-border bg-popover p-1.5 shadow-xl">

          {items.map((i) => (
            <Link
              key={i.to}
              to={localizedPath(i.to, locale)}
              className="block rounded-md px-3 py-2.5 text-sm text-popover-foreground transition-colors hover:bg-muted"
            >
              <div className="font-semibold text-brand">{i.label}</div>
              <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{i.blurb}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductsMenu() {
  const { t, locale } = useI18n();
  return (
    <div className="group relative">
      <Link to={localizedPath("/dscr-loans", locale)} className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-foreground/80 transition-colors hover:text-brand lg:text-sm">
        {t("nav.mortgageLoans")} <Chevron />
      </Link>
      <div className="invisible absolute left-0 top-full z-50 w-[46rem] max-w-[calc(100vw-2rem)] pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="grid max-h-[calc(100vh-6rem)] grid-cols-[minmax(0,1fr)_15rem] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl">
          <div className="p-1.5">
            {products.map((p) => (
              <Link
                key={p.slug}
                to={localizedPath(`/${p.slug}`, locale)}
                className="block rounded-md px-3 py-2.5 text-sm text-popover-foreground transition-colors hover:bg-muted"
              >
                <div className="font-semibold text-brand">{p.shortName}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{p.tagline}</div>
              </Link>
            ))}
            {EXTRA_LOAN_PAGES.map((i) => (
              <Link
                key={i.to}
                to={localizedPath(i.to, locale)}
                className="block rounded-md px-3 py-2.5 text-sm text-popover-foreground transition-colors hover:bg-muted"
              >
                <div className="font-semibold text-brand">{i.label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{i.blurb}</div>
              </Link>
            ))}
          </div>
          <div className="border-l border-border bg-muted/40 p-4">
            <div className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.dscrPrograms")}</div>
            <div className="mt-2 flex flex-col gap-0.5">
              {DSCR_PROGRAMS.map((i) => (
                <Link
                  key={i.to}
                  to={localizedPath(i.to, locale)}
                  className="rounded-md px-2.5 py-1.5 text-sm font-medium text-popover-foreground transition-colors hover:bg-background hover:text-brand"
                >
                  {i.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhereWeLendMenu() {
  const { t, locale } = useI18n();
  return (
    <div className="group relative">
      <Link
        to={localizedPath("/where-we-lend", locale)}
        className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-medium text-foreground/80 transition-colors hover:text-brand lg:text-sm"
      >
        {t("nav.whereWeLend")} <Chevron />
      </Link>
      <div className="invisible absolute left-1/2 top-full z-50 w-[54rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="overflow-hidden rounded-lg border border-border bg-popover shadow-xl">
          <div className="grid grid-cols-[15rem_minmax(0,1fr)]">
            <div className="border-r border-border bg-muted/40 p-4">
              <div className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.loanOptionsByLocation")}</div>
              <div className="mt-2 flex flex-col gap-0.5">
                {products.map((p) => (
                  <Link
                    key={p.slug}
                    to={localizedPath(`/${p.slug}`, locale)}
                    className="rounded-md px-2.5 py-1.5 text-sm font-medium text-popover-foreground transition-colors hover:bg-background hover:text-brand"
                  >
                    {p.shortName}
                  </Link>
                ))}
              </div>
              <Link
                to={localizedPath("/where-we-lend", locale)}
                className="mt-3 block rounded-md bg-brand px-3 py-2 text-center text-xs font-semibold text-primary-foreground transition-colors hover:bg-brand-dark"
              >
                {t("nav.whereWeLendCta")} →
              </Link>
            </div>
            <div className="p-4">
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("nav.statesWeLendIn")}</span>
                <span className="text-[11px] text-muted-foreground">{t("nav.licensingNote")}</span>
              </div>
              <div className="grid max-h-[56vh] grid-cols-3 gap-x-1 gap-y-0.5 overflow-auto pr-1">
                {states.map((s) => (
                  <Link
                    key={s.slug}
                    to={localizedPath("/dscr-loans/$state", locale) as string}
                    params={{ state: s.slug }}
                    className="truncate rounded-md px-2.5 py-1.5 text-sm text-popover-foreground transition-colors hover:bg-muted"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
